import { useEffect, useState } from 'react';
import { generatePath, useParams } from 'react-router';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { DocumentApi } from 'Api/Document/DocumentApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { TprmAssessmentApi } from 'Api/TPRM/TprmAssessmentApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { UcrmAssessmentApi } from 'Api/UCRM/UcrmAssessmentApi';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { InherentRiskQuestionnaireForm, InherentRiskQuestionnaireFormProps } from 'Components/UcrmTprm/InherentRiskQuestionnaireForm/InherentRiskQuestionnaireForm';
import { GENERIC_ERROR_MESSAGE } from 'Config/Errors';
import { TPRM_SERVICE_DASHBOARD, TPRM_THIRD_PARTIES_AND_SERVICES_LISTING, UCRM_BUSINESSES_AND_USE_CASES_LISTING, UCRM_USE_CASE_DASHBOARD } from 'Config/PathTemplates';
import { iso8601ToUsDateLong } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { TprmOrUcrmDueDiligenceWorkflowArchive, getArchivedAssessmentSubject } from 'Models/UcrmTprm';

interface UrlParams {
    service_id: string;
    third_party_id: string;
    timestamp: string;
}

export interface InherentRiskQuestionnaireHistoryProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    assessmentApi: TprmAssessmentApi | UcrmAssessmentApi;
    documentApi: DocumentApi;
    module: DueDiligenceModule;
    moduleApi: ClientTPRMApi | ClientUcrmApi;
}

/** Renders a historical (read-only) version of the IRQ from a specific past due diligence workflow (TPRM or UCRM, per `module`). */
export const InherentRiskQuestionnaireHistory = (props: InherentRiskQuestionnaireHistoryProps) => {
    const params = useParams<keyof UrlParams>() as UrlParams;

    const [loadingErrorOccurred, setLoadingErrorOccurred] = useState(false);
    const [workflowArchive, setWorkflowArchive] = useState<TprmOrUcrmDueDiligenceWorkflowArchive>();

    useEffect(() => {
        const getWorkflowArchive = async () => {
            try {
                const response = await props.assessmentApi.getDueDiligenceWorkflowArchive(params.third_party_id, params.service_id, params.timestamp);
                setWorkflowArchive(response.data);
            } catch (error) {
                console.log(error);
                setLoadingErrorOccurred(true);
            }
        };
        getWorkflowArchive();
    }, [params.third_party_id, params.service_id, params.timestamp, props.assessmentApi]);

    if (loadingErrorOccurred) {
        return <Text>{GENERIC_ERROR_MESSAGE}</Text>;
    }

    if (!workflowArchive) {
        return <Placeholder />;
    }

    const archivedSubject = getArchivedAssessmentSubject(props.module, workflowArchive);

    const inherentRiskQuestionnaireFormProps: InherentRiskQuestionnaireFormProps = {
        artificialIntelligenceApi: props.artificialIntelligenceApi,
        documentApi: props.documentApi,
        module: props.module,
        onFormSubmitBegin: () => void 0,
        onFormSubmitFailed: () => void 0,
        onFormSubmitSucceeded: async () => void 0,
        moduleApi: props.moduleApi,
        userTypeAndData: {
            userHasFullAccess: true,
            historical: true,
            defaultIrq: workflowArchive.irq,
            assessmentSubject: archivedSubject,
        },
    };

    return (
        <>
            <PageLayoutDefault
                headerBreadcrumb={
                    <Breadcrumb textColor="blue">
                        <BreadcrumbLink link={generatePath(props.module === ProductModule.UCRM ? UCRM_BUSINESSES_AND_USE_CASES_LISTING : TPRM_THIRD_PARTIES_AND_SERVICES_LISTING)}>{props.module}</BreadcrumbLink>
                        {<BreadcrumbLink link={generatePath(props.module === ProductModule.UCRM ? UCRM_USE_CASE_DASHBOARD : TPRM_SERVICE_DASHBOARD, { third_party_id: params.third_party_id, service_id: params.service_id })}>{`${archivedSubject.vendor_name} - ${archivedSubject.name}`}</BreadcrumbLink>}
                        <BreadcrumbText>Inherent Risk Questionnaire</BreadcrumbText>
                    </Breadcrumb>
                }
                headerTitle="Inherent Risk Questionnaire"
                headerDescription={archivedSubject.assessment_workflow_data.irq_completed_time ? `Submitted on ${iso8601ToUsDateLong(archivedSubject.assessment_workflow_data.irq_completed_time)}` : undefined}
                body={[
                    {
                        content: <InherentRiskQuestionnaireForm {...inherentRiskQuestionnaireFormProps} />,
                    },
                ]}
            />
        </>
    );
};
